import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useAdminTranslation } from '../utils/useAdminTranslation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LegalNoticePage = () => {
  const { language, tx } = useAdminTranslation();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/site-content/legal`);
      if (response.ok) {
        const data = await response.json();
        if (data.content) {
          setContent(data.content);
        }
      }
    } catch (err) {
      console.error('Error loading legal content:', err);
    }
    setLoading(false);
  };

  const defaultContent = {
    es: {
      title: 'AVISO LEGAL',
      content: `
        <h2 class="text-xl font-bold mt-8 mb-4">DATOS IDENTIFICATIVOS</h2>
        <p>En cumplimiento con el deber de información recogido en artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y del Comercio Electrónico, a continuación se reflejan los siguientes datos:</p>
        <ul class="list-none pl-0 my-4">
          <li><strong>Denominación social:</strong> PUROLINK, S.L.</li>
          <li><strong>CIF:</strong> B 09704610</li>
          <li><strong>Domicilio social:</strong> C/ Santiago 258, bajo izda; 28822; Coslada – Madrid</li>
          <li><strong>Teléfono:</strong> 624224163</li>
          <li><strong>Email:</strong> hola@purolink.com</li>
          <li><strong>Inscrita en:</strong> Registro Mercantil de Madrid</li>
        </ul>

        <h2 class="text-xl font-bold mt-8 mb-4">OBJETO</h2>
        <p>El presente aviso legal regula el uso del sitio web www.purolink.com (en adelante, el "Sitio Web"), del que es titular PUROLINK, S.L. (en adelante, el "Titular").</p>
        <p>La navegación por el Sitio Web atribuye la condición de usuario del mismo e implica la aceptación plena y sin reservas de todas y cada una de las disposiciones incluidas en este Aviso Legal.</p>

        <h2 class="text-xl font-bold mt-8 mb-4">PROPIEDAD INTELECTUAL E INDUSTRIAL</h2>
        <p>El Sitio Web, incluyendo a título enunciativo pero no limitativo su programación, edición, compilación y demás elementos necesarios para su funcionamiento, los diseños, logotipos, texto y/o gráficos son propiedad del Titular o en su caso dispone de licencia o autorización expresa por parte de los autores.</p>
        <p>Todos los contenidos del Sitio Web se encuentran debidamente protegidos por la normativa de propiedad intelectual e industrial.</p>
        <p>Independientemente de la finalidad para la que fueran destinados, la reproducción total o parcial, uso, explotación, distribución y comercialización, requiere en todo caso de la autorización escrita previa por parte del Titular.</p>

        <h2 class="text-xl font-bold mt-8 mb-4">EXENCIÓN DE RESPONSABILIDADES</h2>
        <p>El Titular no se hace responsable, en ningún caso, de los daños y perjuicios de cualquier naturaleza que pudieran ocasionar, a título enunciativo: errores u omisiones en los contenidos, falta de disponibilidad del portal o la transmisión de virus o programas maliciosos o lesivos en los contenidos, a pesar de haber adoptado todas las medidas tecnológicas necesarias para evitarlo.</p>

        <h2 class="text-xl font-bold mt-8 mb-4">MODIFICACIONES</h2>
        <p>El Titular se reserva el derecho de efectuar sin previo aviso las modificaciones que considere oportunas en su portal, pudiendo cambiar, suprimir o añadir tanto los contenidos y servicios que se presten a través de la misma como la forma en la que éstos aparezcan presentados o localizados en su portal.</p>

        <h2 class="text-xl font-bold mt-8 mb-4">ENLACES</h2>
        <p>En el caso de que en el Sitio Web se dispusiesen enlaces o hipervínculos hacía otros sitios de Internet, el Titular no ejercerá ningún tipo de control sobre dichos sitios y contenidos.</p>
        <p>En ningún caso el Titular asumirá responsabilidad alguna por los contenidos de algún enlace perteneciente a un sitio web ajeno, ni garantizará la disponibilidad técnica, calidad, fiabilidad, exactitud, amplitud, veracidad, validez y constitucionalidad de cualquier material o información contenida en ninguno de dichos hipervínculos u otros sitios de Internet.</p>

        <h2 class="text-xl font-bold mt-8 mb-4">DERECHO DE EXCLUSIÓN</h2>
        <p>El Titular se reserva el derecho a denegar o retirar el acceso a portal y/o los servicios ofrecidos sin necesidad de preaviso, a instancia propia o de un tercero, a aquellos usuarios que incumplan las presentes Condiciones Generales de Uso.</p>

        <h2 class="text-xl font-bold mt-8 mb-4">LEGISLACIÓN APLICABLE Y JURISDICCIÓN</h2>
        <p>La relación entre el Titular y el Usuario se regirá por la normativa española vigente y cualquier controversia se someterá a los Juzgados y Tribunales de la ciudad de Madrid.</p>
      `
    },
    en: {
      title: 'LEGAL NOTICE',
      content: `
        <h2 class="text-xl font-bold mt-8 mb-4">IDENTIFICATION DATA</h2>
        <p>In compliance with the duty of information contained in Article 10 of Law 34/2002, of July 11, on Services of the Information Society and Electronic Commerce, the following data is provided:</p>
        <ul class="list-none pl-0 my-4">
          <li><strong>Company name:</strong> PUROLINK, S.L.</li>
          <li><strong>Tax ID:</strong> B 09704610</li>
          <li><strong>Registered office:</strong> C/ Santiago 258, bajo izda; 28822; Coslada – Madrid, Spain</li>
          <li><strong>Phone:</strong> +34 624224163</li>
          <li><strong>Email:</strong> hola@purolink.com</li>
        </ul>

        <h2 class="text-xl font-bold mt-8 mb-4">PURPOSE</h2>
        <p>This legal notice regulates the use of the website www.purolink.com (hereinafter, the "Website"), owned by PUROLINK, S.L. (hereinafter, the "Owner").</p>

        <h2 class="text-xl font-bold mt-8 mb-4">INTELLECTUAL AND INDUSTRIAL PROPERTY</h2>
        <p>The Website, including but not limited to its programming, editing, compilation and other elements necessary for its operation, designs, logos, text and/or graphics are the property of the Owner.</p>

        <h2 class="text-xl font-bold mt-8 mb-4">APPLICABLE LAW AND JURISDICTION</h2>
        <p>The relationship between the Owner and the User shall be governed by current Spanish law and any dispute shall be submitted to the Courts of the city of Madrid.</p>
      `
    }
  };

  const texts = defaultContent[language] || defaultContent.es;
  const displayTitle = content?.title || texts.title;
  const displayContent = content?.content || texts.content;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              {tx('Volver', 'Back')}
            </Button>
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <Scale className="h-6 w-6 text-amber-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">{displayTitle}</h1>
          </div>

          {loading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          ) : (
            <div 
              className="prose prose-gray max-w-none"
              dangerouslySetInnerHTML={{ __html: displayContent }}
            />
          )}

          <div className="mt-12 pt-6 border-t">
            <Link to="/">
              <Button className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                {tx('Volver al inicio', 'Back to home')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalNoticePage;
